import { Box, Button, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import alqemiDataService, { Candidate, Rates } from "../../api/AlqemiDataService";
import CustomPopUp from "../../common/components/utils/CustomPopUp";
import Dropdown from "../../common/components/fundamentals/Dropdown";
import CircularLoader from "../../common/components/fundamentals/Loader/CircularLoader";
import { usePopUp } from "../../common/context/PopUpProvider";
import { useSnackbar } from "../../common/context/SnackbarProvider";
import { HeadingText, SmallText } from "../../common/styles/Text.styles";
import {
  SettingsArea,
  SettingsButtonSection,
} from "../../pages/Settings/Settings.styles";
import {
  borderStyle,
  defaultGap,
  perelynPrimary,
  smallerGap,
  smallGap,
} from "../../styles/consts";

function validateRates(r: Rates): string | null {
  if ([r.rate, r.lower_band, r.upper_band, r.project_volume].some((v) => v < 0)) {
    return "Rates cannot be negative.";
  }
  if (r.project_volume > 100) {
    return "Project volume must be between 0 and 100.";
  }
  if (r.lower_band > 0 && r.upper_band > 0 && r.lower_band > r.upper_band) {
    return "Lower band must be less than or equal to upper band.";
  }
  if (
    r.rate > 0 &&
    r.lower_band > 0 &&
    r.upper_band > 0 &&
    (r.rate < r.lower_band || r.rate > r.upper_band)
  ) {
    return "Rate must lie within the band.";
  }
  return null;
}

const FIELD_LABELS: Record<keyof Rates, string> = {
  rate: "Rate (€/h)",
  lower_band: "Lower band (€/h)",
  upper_band: "Upper band (€/h)",
  project_volume: "Project volume (%)",
};

function formatValue(field: keyof Rates, n: number): string {
  if (n === 0) return "—";
  if (field === "project_volume") {
    return `${Number.isInteger(n) ? n : n.toFixed(2)}%`;
  }
  return `€${Number.isInteger(n) ? n : n.toFixed(2)}`;
}

function diffRows(
  orig: Rates,
  next: Rates,
): { label: string; oldStr: string; newStr: string }[] {
  return (Object.keys(FIELD_LABELS) as (keyof Rates)[])
    .filter((k) => orig[k] !== next[k])
    .map((k) => ({
      label: FIELD_LABELS[k],
      oldStr: formatValue(k, orig[k]),
      newStr: formatValue(k, next[k]),
    }));
}

export default function ManageConsultantRates() {
  const { showSnackbar } = useSnackbar();
  const { showPopUpWindow } = usePopUp();
  const [consultants, setConsultants] = useState<Candidate[]>([]);
  const [isLoadingConsultants, setIsLoadingConsultants] = useState(true);
  const [selectedConsultantId, setSelectedConsultantId] = useState("");
  const [originalRates, setOriginalRates] = useState<Rates | null>(null);
  const [editingRates, setEditingRates] = useState<Rates | null>(null);
  const [isFetchingRates, setIsFetchingRates] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingConsultants(true);
      try {
        const all = await alqemiDataService.getCandidates();
        if (cancelled) return;
        setConsultants(all.filter((c) => !c.archived));
      } catch {
        if (!cancelled) showSnackbar("Error loading consultants", true);
      } finally {
        if (!cancelled) setIsLoadingConsultants(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showSnackbar]);

  const isDirty =
    originalRates !== null &&
    editingRates !== null &&
    JSON.stringify(originalRates) !== JSON.stringify(editingRates);

  const loadConsultantRates = async (id: string) => {
    if (id === "None" || !id) {
      setSelectedConsultantId("");
      setOriginalRates(null);
      setEditingRates(null);
      return;
    }
    setSelectedConsultantId(id);
    setIsFetchingRates(true);
    try {
      const rates = await alqemiDataService.getCandidateRates(id);
      setOriginalRates(rates);
      setEditingRates(rates);
    } catch {
      showSnackbar("Error loading rates", true);
      setOriginalRates(null);
      setEditingRates(null);
    } finally {
      setIsFetchingRates(false);
    }
  };

  const handleConsultantChange = async (id: string) => {
    const apply = () => loadConsultantRates(id);
    if (isDirty) {
      setDropdownKey((k) => k + 1);
      showPopUpWindow(
        apply,
        "Discard unsaved changes?",
        "You have unsaved rate changes. They will be lost.",
        "Cancel",
        "Discard",
      );
      return;
    }
    apply();
  };

  const validationError = editingRates ? validateRates(editingRates) : null;
  const canSave = Boolean(
    selectedConsultantId &&
    editingRates &&
    originalRates &&
    isDirty &&
    !validationError &&
    !isSaving
  );

  const selectedConsultant = consultants.find(
    (c) => (c.consultant_id || c.id) === selectedConsultantId,
  );

  return (
    <SettingsArea>
      <Stack>
        <Stack sx={{ borderBottom: borderStyle }}>
          <Stack sx={{ padding: smallerGap }}>
            <HeadingText>Manage Consultant Rates</HeadingText>
            <SmallText>
              Hourly rates in €/h; project volume in %.
            </SmallText>
          </Stack>
        </Stack>

        <Stack sx={{ padding: `${smallGap} ${defaultGap}`, gap: defaultGap }}>
          {isLoadingConsultants ? (
            <Stack sx={{ alignItems: "center", padding: "2rem" }}>
              <CircularLoader size={"2rem"} />
            </Stack>
          ) : consultants.length === 0 ? (
            <SmallText>No active consultants.</SmallText>
          ) : (
            <Stack sx={{ gap: smallGap }}>
              <Stack>
                <SmallText sx={{ marginBottom: "0.5rem" }}>Select Consultant</SmallText>
                <Dropdown
                  key={dropdownKey}
                  zIndexModifier={100}
                  options={[
                    { value: "None", name: "None" },
                    ...consultants.map((c) => ({
                      value: c.consultant_id || c.id,
                      name: c.name,
                    })),
                  ]}
                  initialSelected={selectedConsultantId || "None"}
                  onChange={handleConsultantChange}
                  width="100%"
                />
              </Stack>

              {selectedConsultantId && (isFetchingRates || !editingRates) ? (
                <Stack sx={{ alignItems: "center", padding: "1rem" }}>
                  <CircularLoader size={"1.5rem"} />
                </Stack>
              ) : selectedConsultantId && editingRates ? (
                <RateFields rates={editingRates} onChange={setEditingRates} disabled={isSaving} />
              ) : null}
            </Stack>
          )}
        </Stack>
      </Stack>

      <SettingsButtonSection>
        <Button
          variant="outlined"
          onClick={() => editingRates && originalRates && setEditingRates(originalRates)}
          disabled={!isDirty || isSaving}
          sx={{
            "&&": { borderBottom: "none", borderTop: "none", borderRight: "none" },
          }}
        >
          Cancel
        </Button>
        <Tooltip
          title={validationError ?? ""}
          disableHoverListener={!validationError}
          disableFocusListener={!validationError}
          disableTouchListener={!validationError}
        >
          {/* Wrapping span — MUI Tooltips don't fire on disabled buttons. */}
          <span title={validationError ?? undefined}>
            <Button
              variant="orange"
              onClick={() => setConfirmOpen(true)}
              disabled={!canSave}
              sx={{ "&&": { border: "none", borderLeft: borderStyle, borderRadius: 0 } }}
            >
              {isSaving ? <CircularProgress size={16} color="inherit" /> : "Save changes"}
            </Button>
          </span>
        </Tooltip>
      </SettingsButtonSection>

      {confirmOpen && originalRates && editingRates && selectedConsultant && (
        <CustomPopUp
          isPopUpOpen={confirmOpen}
          setPopUpOpen={(open) => {
            if (!open) setConfirmOpen(false);
          }}
          heading={`Confirm rate changes — ${selectedConsultant.name}`}
          button1="Cancel"
          button2="Confirm"
          action={async () => {
            if (isSaving) return;
            setIsSaving(true);
            try {
              await alqemiDataService.updateCandidateRates(selectedConsultantId, editingRates);
              setOriginalRates(editingRates);
              showSnackbar("Rates updated", false);
              setConfirmOpen(false);
            } catch (err: any) {
              const status = err?.response?.status;
              const serverMsg = err?.response?.data?.message;
              if (status === 403) {
                showSnackbar("You don't have permission to edit rates", true);
              } else if (status === 404) {
                showSnackbar("Consultant not found — refresh the page", true);
              } else if (status === 400 && serverMsg) {
                showSnackbar(serverMsg, true);
              } else {
                showSnackbar("Failed to save rates. Please try again.", true);
              }
            } finally {
              setIsSaving(false);
            }
          }}
          content={
            <Stack sx={{ gap: "0.4rem" }}>
              {diffRows(originalRates, editingRates).map((row) => (
                <Stack
                  key={row.label}
                  direction="row"
                  sx={{ gap: smallGap, alignItems: "center" }}
                >
                  <Typography sx={{ minWidth: "10rem" }}>{row.label}</Typography>
                  <Typography>{row.oldStr}</Typography>
                  <Typography sx={{ color: "#888" }}>→</Typography>
                  <Typography sx={{ fontWeight: 500 }}>{row.newStr}</Typography>
                </Stack>
              ))}
            </Stack>
          }
        />
      )}
    </SettingsArea>
  );
}

function RateFields({
  rates, onChange, disabled,
}: {
  rates: Rates;
  onChange: (next: Rates) => void;
  disabled?: boolean;
}) {
  const set = (k: keyof Rates) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const num = raw === "" ? 0 : parseFloat(raw);
    if (Number.isNaN(num)) return;
    onChange({ ...rates, [k]: num });
  };
  const display = (n: number) => (n === 0 ? "" : String(n));
  return (
    <Stack sx={{ gap: smallerGap }}>
      <FieldRow label="Rate" value={display(rates.rate)} onChange={set("rate")} step={5} disabled={disabled} />
      <FieldRow label="Lower band" value={display(rates.lower_band)} onChange={set("lower_band")} step={5} disabled={disabled} />
      <FieldRow label="Upper band" value={display(rates.upper_band)} onChange={set("upper_band")} step={5} disabled={disabled} />
      <FieldRow label="Project volume" value={display(rates.project_volume)} onChange={set("project_volume")} step={0.01} disabled={disabled} />
    </Stack>
  );
}

function FieldRow({
  label, value, onChange, step, disabled,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  step: number;
  disabled?: boolean;
}) {
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: smallerGap }}>
      <Typography sx={{ flex: 1 }}>{label}</Typography>
      <Box
        component="input"
        type="number"
        min={0}
        step={step}
        value={value}
        onChange={onChange}
        disabled={disabled}
        sx={{
          width: "8rem",
          padding: "0.25rem 0.5rem",
          border: borderStyle,
          borderRadius: 0,
          outline: "none",
          "&:focus": {
            borderColor: perelynPrimary,
            outline: "none",
          },
          "&:focus-visible": {
            borderColor: perelynPrimary,
            outline: "none",
          },
        }}
      />
    </Stack>
  );
}
